import React from 'react';

import PropTypes from 'prop-types';
import cn from 'classnames';
import { Link } from 'react-router-dom';
import { CgSpinner } from 'react-icons/cg';

import { Text } from 'modules/common';

import * as c from './constants';

function Button({
  leadingItem,
  trailingItem,
  label,
  className,
  isLoading,
  onClick,
  to,
  withScrollOnError,
  type,
  size,
  variant,
  disabled,
}) {
  const $type = c.dispatchType({ variant })?.[type];
  const $size = c.dispatchSize()?.[size];
  const $variant = c.dispatchVariant()?.[variant];

  const handleOnClick = (e) => {
    if (onClick) onClick(e);

    if (!withScrollOnError) return;

    setTimeout(() => {
      let el;

      if (typeof withScrollOnError === 'string') {
        el = document.querySelector(withScrollOnError);
        const topPos = el?.querySelector('.form-error-badge')?.offsetTop || 0;
        el?.scrollTo({ top: topPos / 2, behavior: 'smooth' });
        return;
      }

      el = document.querySelector('.form-error-badge');
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 150);
  };

  const renderButton = () => (
    <button
      type="button"
      className={cn(
        'transition-all duration-150 ease-in-out relative',
        $type,
        $size,
        $variant,
        className,
        {
          'cursor-wait': isLoading,
          'form-solid-button-disabled': disabled && variant === 'solid',
          'form-text-button-disabled': disabled && variant === 'text',
        }
      )}
      onClick={handleOnClick}
      disabled={isLoading || disabled}
    >
      {isLoading ? (
        <button
          className={cn(
            'duration-150 ease-in-out transition-all absolute top-0 left-0 h-full w-full text-center flex !border-0 rounded-sm',
            $type,
            $size,
            $variant,
            className,
            '!p-0'
          )}
          disabled={isLoading}
          style={{ inset: 'auto' }}
          type="button"
        >
          <CgSpinner className="h-5 w-5 animate-spin m-auto" />
        </button>
      ) : null}

      <div className="flex items-center gap-ds-8">
        {leadingItem && <div>{leadingItem}</div>}

        <Text type="caption" weight="semibold">
          {label}
        </Text>

        {trailingItem && <div>{trailingItem}</div>}
      </div>
    </button>
  );

  if (to) {
    return <Link to={to}>{renderButton()}</Link>;
  }

  return renderButton();
}

export default Button;

Button.defaultProps = {
  label: 'Name',
  type: 'default',
  size: 'md',
  variant: 'solid',
  to: '',
  leadingItem: false,
  trailingItem: false,
  withScrollOnError: false,
  onClick: () => {},
  className: '',
  isLoading: false,
  disabled: false,
};

Button.propTypes = {
  label: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.bool,
    PropTypes.element,
    PropTypes.instanceOf(Object),
  ]),
  type: PropTypes.oneOf(Object.keys(c.dispatchType())),
  size: PropTypes.oneOf(Object.keys(c.dispatchSize())),
  variant: PropTypes.oneOf(Object.keys(c.dispatchVariant())),
  withScrollOnError: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  leadingItem: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.string,
    PropTypes.element,
    PropTypes.instanceOf(Object),
  ]),
  trailingItem: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.element,
    PropTypes.string,
    PropTypes.instanceOf(Object),
  ]),
  to: PropTypes.string,
  onClick: PropTypes.instanceOf(Function),
  className: PropTypes.string,
  isLoading: PropTypes.bool,
  disabled: PropTypes.bool,
};
